import { useBulkUpdateTaskDetailsMutation, useFetchTaskAttributesQuery } from 'api/taskApi';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ConfettiButton } from 'ui/components';
import { getIsCompleteTasksFilterActive } from '../../../../store/filtersSlice';
import { getSelectedSiteInfo } from '../../../../store/siteInfoSlice';
import { setCreateModalOpen, setSelectedTaskId } from '../../../../store/tasksSlice';

interface CompleteTaskButtonProps {
    taskId: string;
}

export default function CompleteTaskButton({
    taskId,
}: CompleteTaskButtonProps): JSX.Element {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isCompleteFilterActive = useSelector(getIsCompleteTasksFilterActive);
    const [bulkUpdateTaskDetails, { isLoading }] =
        useBulkUpdateTaskDetailsMutation();
    const siteInfo = useSelector(getSelectedSiteInfo);

    const { currentData: attributes } = useFetchTaskAttributesQuery(taskId);

    const handleClick = async (): Promise<void> => {
        if (isLoading || !taskId || !siteInfo) return;

        const isTaskComplete = attributes?.task_status.some(
            (s) => s.value === 'complete' && s.selected
        );

        try {
            await bulkUpdateTaskDetails({
                websiteId: siteInfo.id,
                updateMethod: 'status',
                taskId,
                value: isTaskComplete ? 'in-progress' : 'complete',  // Toggle status
            }).unwrap().then(() => {
                // close the modal if completed tasks is not in the filtered list
                if (!isCompleteFilterActive) {
                    dispatch(setSelectedTaskId(null));
                    dispatch(setCreateModalOpen(false));
                }
            });
            // toast.success(t('task.status_update.success.in_progress', 'Task marked as In-Progress') : t('task.status_update.success.complete', 'Task marked as Complete'));
        } catch (error) {
            console.error('Failed to update task status:', error);
            // toast.error(t('task.status_update.error', 'Failed to update task status'));
        }
    };

    const isComplete = attributes?.task_status.some(
        (s) => s.value === 'complete' && s.selected
    );


    if (isComplete) {
        return (
            <Button
                variant="muted"
                size="icon"
                onClick={handleClick}
                loading={isLoading}
                tooltip={t('task.complete_button.tooltip.set_in_progress', 'Set this task back to In Progress')}
                tooltipSide="top"
                className="opacity-100 text-primary bg-background hover:bg-primary/10"
            >
                <Check size={18} />
            </Button>
        );
    }

    return (
        <ConfettiButton
            instantFeedback
            variant={'outline'}
            size="icon"
            loading={isLoading}
            tooltip={t('task.complete_button.tooltip.mark_complete', 'Quickly mark this task as complete')}
            tooltipSide="top"
            onClick={handleClick}
        >
            <Check size={18} />
        </ConfettiButton>
    );
}